/**
 * UTM attribution for storefront page views.
 *
 * Campaign parameters only appear on the landing URL, but a visitor usually
 * browses several pages before doing anything interesting. Without persistence
 * every page after the first would be attributed to "direct" and the numbers
 * would understate every campaign.
 *
 * Last-touch: a fresh set of parameters replaces the stored one, matching what
 * merchants expect from analytics tools — the most recent campaign gets credit.
 * Navigating without parameters keeps whatever was stored.
 */

const STORAGE_PREFIX = 'shoppex:utm:';
const TTL_MS = 30 * 24 * 60 * 60 * 1000;

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const;

type UtmKey = (typeof UTM_KEYS)[number];

export type UtmParameters = Partial<Record<UtmKey, string>>;

interface StoredAttribution {
  utm: UtmParameters;
  /** Epoch ms of the last touch, used for TTL expiry. */
  at: number;
}

/** Values are merchant-supplied and land in a text column; keep them bounded. */
const MAX_VALUE_LENGTH = 255;

function readFromUrl(search: string): UtmParameters {
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(search);
  } catch {
    return {};
  }

  const utm: UtmParameters = {};
  for (const key of UTM_KEYS) {
    const value = params.get(key)?.trim();
    if (value) {
      utm[key] = value.slice(0, MAX_VALUE_LENGTH);
    }
  }
  return utm;
}

function storageKey(storeSlug: string): string {
  return `${STORAGE_PREFIX}${storeSlug}`;
}

function readStored(storeSlug: string): UtmParameters {
  try {
    const raw = window.localStorage.getItem(storageKey(storeSlug));
    if (!raw) return {};

    const parsed = JSON.parse(raw) as StoredAttribution;
    if (!parsed || typeof parsed.at !== 'number' || typeof parsed.utm !== 'object') return {};
    if (Date.now() - parsed.at > TTL_MS) {
      window.localStorage.removeItem(storageKey(storeSlug));
      return {};
    }

    const utm: UtmParameters = {};
    for (const key of UTM_KEYS) {
      const value = parsed.utm?.[key];
      if (typeof value === 'string' && value.length > 0) {
        utm[key] = value.slice(0, MAX_VALUE_LENGTH);
      }
    }
    return utm;
  } catch {
    // Private mode, quota errors, corrupt JSON — attribution is best-effort and
    // must never break a page view.
    return {};
  }
}

function writeStored(storeSlug: string, utm: UtmParameters): void {
  try {
    const payload: StoredAttribution = { utm, at: Date.now() };
    window.localStorage.setItem(storageKey(storeSlug), JSON.stringify(payload));
  } catch {
    // Ignore: the current page view still reports the parameters it just read.
  }
}

/**
 * Resolves the UTM parameters to report for the current page view.
 *
 * Reads the current URL first; when it carries any campaign parameter that set
 * wins and is persisted. Otherwise the stored set is returned unchanged.
 *
 * @returns the parameters, or undefined when there is nothing to report
 */
export function resolveUtmParameters(storeSlug: string): UtmParameters | undefined {
  if (typeof window === 'undefined' || typeof window.location === 'undefined') {
    return undefined;
  }

  const fromUrl = readFromUrl(window.location.search);
  if (Object.keys(fromUrl).length > 0) {
    writeStored(storeSlug, fromUrl);
    return fromUrl;
  }

  const stored = readStored(storeSlug);
  return Object.keys(stored).length > 0 ? stored : undefined;
}
